import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import type { ApiResponse } from "@/types/api";

export interface AuthUser {
  id: number;
  email: string;
  nombre: string;
  rol_id: number;
  permisos: string[];
  empresas: { id: number; nombre: string }[];
  empresa_id: number | null;
}

interface LoginResponsePayload {
  status: "success";
  msg: string;
  redirect_url: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permiso: string) => boolean;
  isAdmin: boolean;
  login: (email: string, password: string, remember_me: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch<ApiResponse<AuthUser>>("/api/v1/auth/me");
      setUser((res.data ?? null) as AuthUser | null);
    } catch {
      setUser(null);
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string, remember_me: boolean) => {
      setError(null);
      const res = await apiFetch<LoginResponsePayload>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, remember_me }),
      });
      if (res.status !== "success") {
        throw new Error(res.msg ?? "Login fallido");
      }
      const me = await apiFetch<ApiResponse<AuthUser>>("/api/v1/auth/me");
      setUser((me.data ?? null) as AuthUser | null);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch<unknown>("/api/v1/auth/logout", { method: "POST" });
    } catch {
      // ignore — proceed to redirect
    }
    queryClient.clear();
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate, queryClient]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const res = await apiFetch<ApiResponse<AuthUser>>("/api/v1/auth/me");
        if (!cancelled) setUser((res.data ?? null) as AuthUser | null);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      if (!window.location.pathname.startsWith("/login")) {
        const target = `/login?from=${encodeURIComponent(window.location.pathname)}`;
        queryClient.clear();
        navigate(target, { replace: true });
        setUser(null);
      }
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => {
      window.removeEventListener("auth:unauthorized", onUnauthorized);
    };
  }, [navigate, queryClient]);

  const hasPermission = useCallback(
    (permiso: string) => {
      if (!user) return false;
      if (user.rol_id === 1) return true;
      return user.permisos.includes(permiso);
    },
    [user],
  );

  const isAdmin = user?.rol_id === 1;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      hasPermission,
      isAdmin: Boolean(isAdmin),
      login,
      logout,
      refresh,
    }),
    [user, loading, error, hasPermission, isAdmin, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return ctx;
};
